/**
 * Agent OS shared lead-form helpers.
 *
 * Exports:
 *   - AgentOsExitPopup        — exit-intent lead magnet (PDF playbook)
 *   - LiveLeadCounter         — animated social proof ticker
 *   - CalendlySuccessEmbed    — post-submit demo booking embed
 *   - AgentOsPixelTracker     — Meta Pixel + GA4 event fire on key actions
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { X, ArrowRight, Calendar, TrendingUp, Sparkles, Loader2 } from "lucide-react";
import { API } from "../../config";
import { trackEvent } from "../../lib/analytics";

const FB_PIXEL_ID = process.env.REACT_APP_FB_PIXEL_ID || "";
const CALENDLY_URL = process.env.REACT_APP_AGENT_OS_CALENDLY_URL || "";

/* ------------------------------------------------------------------ */
/* Exit-intent popup                                                  */
/* ------------------------------------------------------------------ */

const COPY = {
  agency: {
    eyebrow: "Špeciálne pre teba",
    h1: "Stiahni AI Agency Playbook",
    desc: "12-stranový PDF: presný plán, ako tvoja agentúra pridá €1 500+ MRR per klient bez najímania ľudí.",
    cta: "Stiahnuť PDF (zdarma)",
    placeholder: "Tvoj email",
    accent: "yellow",
    funnel: "agent_os_agency",
  },
  eshop: {
    eyebrow: "Pred odchodom",
    h1: "Stiahni Agent OS Cheatsheet",
    desc: "1-strana A4: ktorý AI agent rieši ktorú konkrétnu bolesť. Plus 14-dňový trial bez karty.",
    cta: "Stiahnuť cheatsheet",
    placeholder: "Tvoj email",
    accent: "emerald",
    funnel: "agent_os_eshop",
  },
};

export function AgentOsExitPopup({ variant = "agency" }) {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({ loading: false, success: false, error: null });
  const copy = COPY[variant] || COPY.agency;
  const storageKey = `trp_agent_os_exit_${variant}`;

  const onMouseLeave = useCallback((e) => {
    if (e.clientY <= 5 && !dismissed && !sessionStorage.getItem(storageKey)) {
      setShow(true);
      sessionStorage.setItem(storageKey, "1");
      trackEvent("agent_os_exit_popup_shown", { variant });
    }
  }, [dismissed, storageKey, variant]);

  useEffect(() => {
    if (window.innerWidth < 768) return;
    if (sessionStorage.getItem(storageKey)) return;
    document.addEventListener("mouseleave", onMouseLeave);
    return () => document.removeEventListener("mouseleave", onMouseLeave);
  }, [onMouseLeave, storageKey]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus({ loading: true, success: false, error: null });
    try {
      const endpoint = variant === "eshop" ? "/agent-os/leads/eshop" : "/agent-os/leads/agency";
      await axios.post(`${API}${endpoint}`, { email, source: `exit_popup_${variant}` });
      setStatus({ loading: false, success: true, error: null });
      trackEvent("agent_os_exit_popup_converted", { variant, email });
    } catch (err) {
      setStatus({ loading: false, success: false, error: "Skús ešte raz." });
    }
  };

  if (!show || dismissed) return null;
  const accentBg = copy.accent === "yellow" ? "bg-yellow-400 hover:bg-yellow-300" : "bg-emerald-400 hover:bg-emerald-300";
  const accentBorder = copy.accent === "yellow" ? "border-yellow-700/60" : "border-emerald-700/60";
  const accentText = copy.accent === "yellow" ? "text-yellow-400" : "text-emerald-400";

  return (
    <div
      data-testid={`agent-os-exit-popup-${variant}`}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
    >
      <div className={`relative max-w-md w-full bg-[#0a0a0a] border-2 ${accentBorder} p-8`}>
        <button
          onClick={() => { setDismissed(true); setShow(false); }}
          data-testid={`agent-os-exit-popup-close-${variant}`}
          className="absolute top-3 right-3 text-gray-500 hover:text-white"
          aria-label="Zavri"
        >
          <X className="w-5 h-5" />
        </button>

        {status.success ? (
          <div className="text-center" data-testid={`agent-os-exit-popup-success-${variant}`}>
            <Sparkles className={`w-12 h-12 mx-auto mb-4 ${accentText}`} />
            <h2 className="text-2xl font-black mb-2">Hotovo! 🎉</h2>
            <p className="text-sm text-gray-400 mb-5">PDF + onboarding email idú do tvojho inboxu. Spam priečinok pre istotu.</p>
            <button
              onClick={() => { setDismissed(true); setShow(false); }}
              className={`px-5 py-2 ${accentBg} text-black text-xs font-black uppercase tracking-widest`}
            >
              Zavri
            </button>
          </div>
        ) : (
          <>
            <div className={`text-[10px] uppercase tracking-[0.25em] ${accentText} font-bold mb-2`}>
              {copy.eyebrow}
            </div>
            <h2 className="text-3xl font-black mb-3 leading-tight">{copy.h1}</h2>
            <p className="text-sm text-gray-400 mb-5 leading-relaxed">{copy.desc}</p>
            <form onSubmit={submit} className="space-y-3">
              <input
                data-testid={`agent-os-exit-popup-email-${variant}`}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={copy.placeholder}
                className={`w-full px-4 py-3 bg-black border border-gray-800 focus:${accentBorder} outline-none text-white placeholder-gray-600`}
              />
              {status.error && (
                <div className="text-rose-300 text-xs">{status.error}</div>
              )}
              <button
                type="submit"
                disabled={status.loading}
                data-testid={`agent-os-exit-popup-submit-${variant}`}
                className={`w-full px-5 py-3 ${accentBg} disabled:opacity-50 text-black font-black uppercase tracking-widest text-sm flex items-center justify-center gap-2`}
              >
                {status.loading
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Posielam…</>
                  : <>{copy.cta} <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
            <p className="text-[10px] text-gray-600 text-center mt-3">Bez spamu · GDPR · 1-click unsubscribe</p>
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Live social proof counter                                          */
/* ------------------------------------------------------------------ */

export function LiveLeadCounter({ variant = "agency" }) {
  const [stats, setStats] = useState({ agency_count: 47, eshop_count: 23, total_demos: 0 });

  useEffect(() => {
    let alive = true;
    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${API}/agent-os/stats/social-proof`);
        if (alive) setStats(r.data || stats);
      } catch { /* keep floor */ }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 30000); // refresh every 30s
    return () => { alive = false; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const count = variant === "eshop" ? stats.eshop_count : stats.agency_count;
  const label = variant === "eshop" ? "e-shopov používa Agent OS" : "agentúr v beta programe";
  const accent = variant === "eshop" ? "text-emerald-400" : "text-yellow-400";
  const dotColor = variant === "eshop" ? "bg-emerald-400" : "bg-yellow-400";

  return (
    <div
      data-testid={`agent-os-live-counter-${variant}`}
      className="inline-flex items-center gap-3 px-4 py-2 bg-black/60 border border-gray-800"
    >
      <span className={`relative inline-flex h-2 w-2`}>
        <span className={`absolute inline-flex h-full w-full rounded-full ${dotColor} opacity-75 animate-ping`} />
        <span className={`relative inline-flex h-2 w-2 rounded-full ${dotColor}`} />
      </span>
      <span className="text-xs text-gray-300 font-mono uppercase tracking-widest">
        <span className={`font-black ${accent} text-base tabular-nums`}>{count.toLocaleString("sk-SK")}</span>
        {" "}{label}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Calendly success embed                                             */
/* ------------------------------------------------------------------ */

export function CalendlySuccessEmbed({ accent = "yellow" }) {
  const accentBg = accent === "yellow" ? "bg-yellow-400 hover:bg-yellow-300" : "bg-emerald-400 hover:bg-emerald-300";
  const accentText = accent === "yellow" ? "text-yellow-400" : "text-emerald-400";

  if (!CALENDLY_URL) {
    return (
      <div className="mt-8 p-5 border border-gray-800 bg-black/40 text-center" data-testid="agent-os-calendly-placeholder">
        <Calendar className={`w-6 h-6 mx-auto mb-3 ${accentText}`} />
        <p className="text-sm text-gray-400 mb-4">
          Bookni si demo call (15 min, zdarma) — pošleme link emailom hneď ako bude tvoj account ready.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-8" data-testid="agent-os-calendly-embed">
      <div className="text-center mb-4">
        <Calendar className={`w-6 h-6 mx-auto mb-2 ${accentText}`} />
        <p className="text-sm text-gray-300 font-bold">Bookni si demo call (15 min)</p>
      </div>
      <a
        href={CALENDLY_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="agent-os-calendly-cta"
        onClick={() => trackEvent("agent_os_calendly_clicked", { accent })}
        className={`inline-flex items-center justify-center gap-2 w-full px-6 py-3 ${accentBg} text-black font-black uppercase tracking-wider text-sm`}
      >
        Vybrať čas v kalendári <ArrowRight className="w-4 h-4" />
      </a>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Meta Pixel + GA4 helper                                            */
/* ------------------------------------------------------------------ */

export function AgentOsPixelTracker({ event = "Lead", value = 0, currency = "EUR" }) {
  useEffect(() => {
    // Meta Pixel — only if configured.
    if (FB_PIXEL_ID && typeof window !== "undefined") {
      if (!window.fbq) {
        // Standard Meta Pixel snippet.
        (function (f, b, e, v, n, t, s) {
          if (f.fbq) return;
          n = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
          };
          if (!f._fbq) f._fbq = n;
          n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
          t = b.createElement(e); t.async = !0;
          t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
        })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
        window.fbq("init", FB_PIXEL_ID);
        window.fbq("track", "PageView");
      }
      if (event) {
        window.fbq("track", event, { value, currency });
      }
    }
    // GA4 — always fired via existing analytics lib (no-op if not configured).
    trackEvent(`agent_os_${event.toLowerCase()}`, { value, currency });
  }, [event, value, currency]);

  return null;
}

export function Eyebrow({ children, color = "yellow" }) {
  const cls = color === "emerald" ? "text-emerald-400" : "text-yellow-400";
  return (
    <div className={`text-xs uppercase tracking-[0.3em] ${cls} font-bold mb-3`}>{children}</div>
  );
}
